<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pb-0">
        <div class="tw-flex tw-justify-between tw-items-center">
          <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin tw-my-auto">
            My Activities
          </h1>
          <base-button v-show="!isLocationFiltered" @click="add" class="tw-hidden sm:tw-flex">
            <template v-slot:icon
              ><plus-icon
            /></template>
            New Activity
          </base-button>
          <base-icon @click="add" class="tw-inline sm:tw-hidden">
            <plus-icon class="tw-h-5 tw-w-5" />
          </base-icon>
        </div>
        <base-tabs class="tw-mt-2 tw-pb-4 md:tw-pb-0 tw-mb-0" no-border @click="tabClick($event)">
          <base-tab v-for="tab in tabs" :key="tab.title" :selected="$route.meta.component === tab.component" :title="tab.title" />
        </base-tabs>
      </base-content>
    </div>

    <base-content>
      <base-text-field v-model="query" class="w-full" placeholder="Search for a patient" size="sm">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>

      <div id="filter" class="tw-mb-2.5 tw-flex tw-justify-end">
        <base-button @click="$refs.filterSlideover.show()" class="tw-mb-2 tw-mt-3 tw-pr-0" color="primary" size="sm" type="flat">
          <template v-slot:icon
            ><filter-icon
          /></template>
          Filters
          <base-badge class="tw-ml-2" color="primary" size="sm">{{ countFilters }}</base-badge>
        </base-button>
      </div>

      <!-- Loading state while initial data loads -->
      <div v-if="!isDataReady" class="tw-flex tw-justify-center tw-py-10">
        <base-spinner />
      </div>

      <!-- Show component only when data is ready -->
      <div v-else class="tw-overflow-auto tw-rounded-lg">
        <component
          :is="currentComponent"
          :business-id="businessId"
          :query="query"
          :filters="filters"
          :is-data-ready="isDataReady"
          @deny="deny"
          @approve="approve"
          @assign="assign"
          @comment="comment"
          @del="del"
          @history="history"
        />
      </div>
    </base-content>

    <filter-slideover
      ref="filterSlideover"
      :businessId="businessId"
      @filter-updated="applyFilter"
      :isCompletedTab="currentTab === 'CompletedTodoView'"
      :isCompletedByMeTab="currentTab === 'CompletedByMeTodoView'"
      :isLocationTodoTab="true"
    />

    <!-- Modal Components for Activity Management -->
    <new-todo-item ref="newTodoModal" :businessId="businessId" />
    <assign-todo-item ref="assignModal" :businessId="businessId" />
    <delete-todo-item ref="deleteModal" :businessId="businessId" />
    <add-note ref="addNoteModal" :businessId="businessId" />
    <view-details ref="historyModal" :businessId="businessId" />
    <deny-rx-auth ref="denyModal" :businessId="businessId" />
    <approve-rx-auth ref="approveModal" :businessId="businessId" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

import FilterSlideover from "@/views/business/todo/components/FilterSlideover";
import NewTodoItem from "@/views/business/todo/components/NewTodoItem";
import AssignTodoItem from "@/views/business/todo/components/AssignTodoItem";
import DeleteTodoItem from "@/views/business/todo/components/DeleteTodoItem";
import { PlusIcon, SearchIcon, FilterIcon } from "@/assets/svgs";
import AddNote from "@/views/business/todo/components/AddNote";
import ViewDetails from "@/views/business/todo/components/ViewDetails";
import ApproveRxAuth from "@/views/business/todo/components/ApproveRxAuth";
import DenyRxAuth from "@/views/business/todo/components/DenyRxAuth";

export default {
  name: "LocationTodoView",
  props: {
    businessId: String,
  },
  components: {
    FilterSlideover,
    NewTodoItem,
    AssignTodoItem,
    DeleteTodoItem,
    AddNote,
    ViewDetails,
    ApproveRxAuth,
    DenyRxAuth,
    PlusIcon,
    SearchIcon,
    FilterIcon,
    DueTodayTodoView: () => import("@/views/business/todo/location-todo/DueTodayTodoView"),
    PastDueTodoView: () => import("@/views/business/todo/location-todo/PastDueTodoView"),
    FollowUpTodoView: () => import("@/views/business/todo/location-todo/FollowUpTodoView"),
    AllTodoView: () => import("@/views/business/todo/location-todo/AllTodoView"),
    CompletedTodoView: () => import("@/views/business/todo/location-todo/CompletedTodoView"),
    CompletedByMeTodoView: () => import("@/views/business/todo/location-todo/CompletedByMeTodoView"),
  },
  data() {
    return {
      query: null,
      itemTypes: [],
      filters: {
        locationsFilter: [],
        typesFilter: [],
        assignedToFilter: [],
        startDate: null,
        endDate: null,
        completedStartDate: null,
        completedEndDate: null,
        completedByFilter: [],
      },
      isDataReady: false,
    };
  },
  computed: {
    ...mapGetters("role", { isLocationFiltered: "isLocationFiltered" }),
    ...mapGetters("organization", [
      "organization",
      "organizations",
      "isOrganizationPredictile",
      "isOrganizationBusiness",
      "isOrganizationUser",
      "hasMultipleOrganizations",
      "cobrandId",
    ]),
    currentComponent() {
      return this.$route.meta.component;
    },
    currentTab() {
      return this.$route.meta.component;
    },
    tabs() {
      return this.cobrandId === "rx-harmony"
        ? [
            { title: "Past Due", component: "PastDueTodoView", route: "past-due" },
            { title: "Follow Up", component: "FollowUpTodoView", route: "follow-up" },
            { title: "Completed", component: "CompletedByMeTodoView", route: "completed-by-me" },
          ]
        : [
            { title: "Due Today", component: "DueTodayTodoView", route: "due-today" },
            { title: "Past Due", component: "PastDueTodoView", route: "past-due" },
            { title: "Follow Up", component: "FollowUpTodoView", route: "follow-up" },
            { title: "All", component: "AllTodoView", route: "all" },
            { title: "Completed", component: "CompletedTodoView", route: "completed" },
          ];
    },
    countFilters() {
      let count = 0;
      if (this.filters.locationsFilter.length) count += this.filters.locationsFilter.length;
      if (this.filters.typesFilter.length) count += this.filters.typesFilter.length;
      if (this.filters.assignedToFilter.length) count += this.filters.assignedToFilter.length;
      if (this.filters.startDate || this.filters.endDate) count++;
      if (this.filters.completedStartDate || this.filters.completedEndDate) count++;
      if (this.filters.completedByFilter.length) count += this.filters.completedByFilter.length;
      return count;
    },
  },
  watch: {
    currentTab(newTab, oldTab) {
      if ((oldTab === "CompletedTodoView" || oldTab === "CompletedByMeTodoView") && newTab !== "CompletedTodoView" && newTab !== "CompletedByMeTodoView") {
        this.clearCompletedFilters();
      }
    },
  },
  async mounted() {
    await this.initializeData();
  },
  methods: {
    async initializeData() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const hasUrlFilter = urlParams.has("filters");

        await Promise.all([this.loadItemTypes()]);

        // Load filters if present in URL
        if (hasUrlFilter) {
          await this.loadFiltersFromUrl();
        }

        // Mark data as ready
        this.isDataReady = true;
      } catch (error) {
        console.error("Error during data initialization:", error);
        this.isDataReady = true;
      }
    },

    async loadItemTypes() {
      const res = await this.$axios.get(`/businesses/${this.businessId}/todo/types`);
      this.itemTypes = res.data;
    },

    async loadFiltersFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.has("filters")) return;

      try {
        const filters = JSON.parse(decodeURIComponent(urlParams.get("filters")));
        const typeFilterIds = Array.isArray(filters.typesFilter) ? filters.typesFilter : [filters.typesFilter];

        this.filters = {
          locationsFilter: filters.locationsFilter || [],
          typesFilter: typeFilterIds || [],
          assignedToFilter: filters.assignedToFilter || [],
          startDate: filters.startDate ? moment(filters.startDate, "YYYY-MM-DD").toDate() : null,
          endDate: filters.endDate ? moment(filters.endDate, "YYYY-MM-DD").toDate() : null,
          completedStartDate: filters.completedStartDate ? moment(filters.completedStartDate, "YYYY-MM-DD").toDate() : null,
          completedEndDate: filters.completedEndDate ? moment(filters.completedEndDate, "YYYY-MM-DD").toDate() : null,
          completedByFilter: filters.completedByFilter || [],
        };

        await this.$nextTick();

        if (this.$refs.filterSlideover) {
          await this.syncFiltersToSlideover(filters);
        }
      } catch (error) {
        console.error("Error parsing filters from URL:", error);
      }
    },

    async syncFiltersToSlideover(urlFilters) {
      const filterSlideover = this.$refs.filterSlideover;
      if (!filterSlideover) return;

      const matchedTypes = this.itemTypes.filter((item) => this.filters.typesFilter.includes(item.todoItemTypeId));

      filterSlideover.typesFilter = matchedTypes;
      filterSlideover.locationsFilter = filterSlideover.orderedLocations.filter((loc) => this.filters.locationsFilter.includes(loc.businessLocationId)) || [];
      filterSlideover.assignedToFilter = filterSlideover.businessUsers.filter((user) => this.filters.assignedToFilter.includes(user.userId)) || [];
      filterSlideover.completedByFilter = filterSlideover.businessUsers.filter((user) => this.filters.completedByFilter.includes(user.userId)) || [];
      filterSlideover.startDate = this.filters.startDate;
      filterSlideover.endDate = this.filters.endDate;
      filterSlideover.completedStartDate = this.filters.completedStartDate;
      filterSlideover.completedEndDate = this.filters.completedEndDate;

      const activeFilter = urlFilters.dateToggle || this.getDateShortcut(this.filters.startDate, this.filters.endDate);

      filterSlideover.activeFilter = activeFilter;
      filterSlideover.showDateFields = activeFilter === "custom";

      await this.$nextTick();

      if (activeFilter && filterSlideover.$refs.dateToggle && typeof filterSlideover.$refs.dateToggle.selectOption === "function") {
        try {
          filterSlideover.$refs.dateToggle.selectOption(activeFilter);
        } catch (error) {
          console.warn("Could not select date toggle option:", error);
        }
      }
    },

    getDateShortcut(startDate, endDate) {
      if (!startDate || !endDate) return null;

      const start = moment(startDate).startOf("day");
      const end = moment(endDate).startOf("day");
      const today = moment().startOf("day");

      if (start.isSame(today) && end.isSame(today)) return "today";

      const yesterday = moment()
        .subtract(1, "day")
        .startOf("day");
      if (start.isSame(yesterday) && end.isSame(yesterday)) return "yesterday";

      const startOfWeek = moment().startOf("isoWeek");
      const endOfWeek = moment().endOf("isoWeek");
      if (start.isSame(startOfWeek) && end.isSame(endOfWeek, "day")) return "week";

      const startOfMonth = moment().startOf("month");
      const endOfMonth = moment().endOf("month");
      if (start.isSame(startOfMonth) && end.isSame(endOfMonth, "day")) return "month";

      return "custom";
    },

    clearCompletedFilters() {
      this.filters.completedStartDate = null;
      this.filters.completedEndDate = null;
      this.filters.completedByFilter = [];

      if (this.$refs.filterSlideover) {
        this.$refs.filterSlideover.completedStartDate = null;
        this.$refs.filterSlideover.completedEndDate = null;
        this.$refs.filterSlideover.completedByFilter = [];
      }
    },

    applyFilter(filters) {
      this.filters = filters;
    },

    assign(item) {
      this.$refs.assignModal.show(item);
    },

    approve(item) {
      this.$refs.approveModal.show(item);
    },

    deny(item) {
      this.$refs.denyModal.show(item);
    },

    comment(item) {
      this.$refs.addNoteModal.show(item.todoItemId, item.constant);
    },

    history(item) {
      this.$refs.historyModal.show(item);
    },

    del(item) {
      this.$refs.deleteModal.show(item);
    },

    tabClick(tabTitle) {
      const tab = this.tabs.find((t) => t.title === tabTitle);
      if (tab) {
        this.$router.push({
          path: `/businesses/${this.businessId}/location-todo/${tab.route}`,
          params: { businessId: this.businessId },
        });
      }
    },

    add() {
      this.$refs.newTodoModal.show();
    },
  },
};
</script>
